/**
 * Star toggle to follow/unfollow a scenario — the same control as the star on a
 * scenario card, surfaced anywhere a scenario appears (e.g. the /followed page).
 * Backed by scenarioStorage bookmarks and kept in sync across instances via the
 * 'ahp-scenario-bookmarks-changed' event.
 */
import React, { useEffect, useState } from "react";
import { Star } from "lucide-react";
import { scenarioStorage } from "@/lib/scenarioStorage";

interface Props {
  scenarioId: string;
  dark?: boolean;
  size?: number;
  className?: string;
  withLabel?: boolean;
}

export default function FollowScenarioButton({ scenarioId, dark, size = 16, className = "", withLabel = false }: Props) {
  const [followed, setFollowed] = useState(false);

  useEffect(() => {
    const sync = () => setFollowed(scenarioStorage.isBookmarked(scenarioId));
    sync();
    window.addEventListener("ahp-scenario-bookmarks-changed", sync);
    return () => window.removeEventListener("ahp-scenario-bookmarks-changed", sync);
  }, [scenarioId]);

  const gold = "var(--civic-gold)";
  const idle = dark ? "rgba(var(--civic-warm-rgb),0.55)" : "rgba(var(--civic-ink-rgb),0.45)";

  return (
    <button
      type="button"
      aria-label={followed ? "Unfollow scenario" : "Follow scenario"}
      aria-pressed={followed}
      title={followed ? "Following — tap to remove from your shortlist" : "Follow this scenario — save it to your shortlist"}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        scenarioStorage.toggleBookmark(scenarioId);
        setFollowed(scenarioStorage.isBookmarked(scenarioId));
      }}
      className={`inline-flex items-center gap-1 transition-colors ${className}`}
      style={{ color: followed ? gold : idle }}
    >
      <Star style={{ width: size, height: size }} fill={followed ? gold : "none"} strokeWidth={2} />
      {withLabel && <span style={{ fontSize: 12, fontWeight: 600 }}>{followed ? "Following" : "Follow"}</span>}
    </button>
  );
}
